import {
  KnockGuideProvider as RawKnockGuideProvider,
  KnockProvider as RawKnockProvider,
  useKnockClient,
  useNotifications,
} from '@knocklabs/react-core'
// oxlint-disable-next-line no-restricted-imports
import { useMedplumProfile } from '@medplum/react-hooks'
import { featureFlags } from '@seen/core/featureFlags'
import { toKnockId } from '@seen/core/knock'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { isOfType } from '@seen/core/utils'
import { type ReactNode, useMemo } from 'react'
import { buildKnockPractitionerUserProfile } from '#src/components/KnockProvider.utils.ts'
import { InboxLiveSyncContext } from '#src/contexts/InboxLiveSyncContext.ts'
import {
  KnockDoneFeedContext,
  KnockInboxFeedContext,
} from '#src/contexts/KnockFeedClientContext.tsx'
import { useInboxLiveSync } from '#src/hooks/inbox/useInboxLiveSync.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useImpersonateKnock } from '#src/hooks/useImpersonateKnock.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface KnockProviderProps {
  knockApiKey: string
  knockFeedChannelId: string
  knockGuideChannelId?: string
  children: ReactNode
}

export function KnockProvider({
  knockApiKey,
  knockFeedChannelId,
  knockGuideChannelId,
  children,
}: KnockProviderProps): ReactNode {
  // we don't use useCurrentPractitioner because it's possible to be logged out
  const profile = useMedplumProfile()
  const currentPractitioner = useMemo(
    () =>
      profile && isOfType(profile, 'Practitioner')
        ? ExtendedPractitioner.fromFhirResource(profile)
        : undefined,
    [profile]
  )

  const impersonatedPractitioner = useImpersonateKnock(
    (state) => state.impersonatedPractitioner
  )

  const practitionerReference =
    impersonatedPractitioner ?? currentPractitioner?.reference
  const practitionerKnockId = practitionerReference
    ? toKnockId(practitionerReference)
    : undefined
  const isImpersonating = !!impersonatedPractitioner
  const knockUser = useMemo(() => {
    if (!practitionerKnockId) {
      return undefined
    }

    if (isImpersonating || !currentPractitioner) {
      return { id: practitionerKnockId }
    }

    return {
      id: practitionerKnockId,
      ...buildKnockPractitionerUserProfile(currentPractitioner),
    }
  }, [currentPractitioner, isImpersonating, practitionerKnockId])

  if (!knockUser) {
    return children
  }

  return (
    <RawKnockProvider key={knockUser.id} apiKey={knockApiKey} user={knockUser}>
      <FeedProvider knockFeedChannelId={knockFeedChannelId}>
        <GuideProvider knockGuideChannelId={knockGuideChannelId}>
          {children}
        </GuideProvider>
      </FeedProvider>
    </RawKnockProvider>
  )
}

function GuideProvider({
  knockGuideChannelId,
  children,
}: {
  knockGuideChannelId?: string | undefined
  children: ReactNode
}): ReactNode {
  if (!knockGuideChannelId) {
    return children
  }

  return (
    <RawKnockGuideProvider
      channelId={knockGuideChannelId}
      readyToTarget={true}
      targetParams={{ data: { app: 'orbit' } }}
      trackLocationFromWindow={true}
    >
      {children}
    </RawKnockGuideProvider>
  )
}

function FeedProvider({
  knockFeedChannelId,
  children,
}: {
  knockFeedChannelId: string
  children: ReactNode
}): ReactNode {
  const knockClient = useKnockClient()

  const inboxFeed = useNotifications(knockClient, knockFeedChannelId, {
    archived: 'exclude',
  })

  const doneFeed = useNotifications(knockClient, knockFeedChannelId, {
    archived: 'include',
  })

  /* Bound explicitly to inboxFeed. Both feeds share a BroadcastChannel name —
     it is built from the feed id and user, which do not encode the archived
     query — so exactly one coordinator may listen, and it must know which store
     it is allowed to write to. */
  const inboxLiveSyncEnabled = useFeatureFlag(featureFlags.inboxTrust)
  const medplum = useMedplum()
  const inboxLiveSync = useInboxLiveSync({
    feedClient: inboxFeed,
    medplum,
    enabled: inboxLiveSyncEnabled,
  })

  return (
    <KnockInboxFeedContext.Provider value={inboxFeed}>
      <KnockDoneFeedContext.Provider value={doneFeed}>
        <InboxLiveSyncContext.Provider value={inboxLiveSync}>
          {children}
        </InboxLiveSyncContext.Provider>
      </KnockDoneFeedContext.Provider>
    </KnockInboxFeedContext.Provider>
  )
}
